import { useEffect, useState } from "react";
import { useFirstExportToast } from "@/components/onboarding/useFirstExportToast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/lilia-ui/dialog";
import { Button } from "@/components/lilia-ui/button";
import { Checkbox } from "@/components/lilia-ui/checkbox";
import { Progress } from "@/components/lilia-ui/progress";
import { Switch } from "@/components/lilia-ui/switch";
import type { DocumentLook, ThemeId } from "@/lib/api";
import { useThemes } from "@/hooks/useThemes";
import { effectiveTheme, themeAllowed, unavailableReason } from "@/lib/themes/look";
import { pdfExportParams } from "@/lib/themes/exportParams";
import { testIds } from "@/testIds";
import { useApiContext, API_BASE } from "@/contexts/ApiContext";
import { useLicense } from "@/contexts/LicenseContext";
import { cn } from "@/lib/cn";
import { useTranslation } from "@/utils/hooks/useTranslation";

// All exporters are server-side. The editor SPA never produces export
// bytes itself — it just calls the API and downloads the response. See
// memory: feedback_exports_api_only.md.

type PdfEngine = "auto" | "typst" | "pdflatex";

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  documentId: string;
  documentTitle: string;
  /** The document's own look; the PDF row starts from it. */
  look?: DocumentLook | null;
  /** Why themes are off for this document (a publisher class), or null. */
  lookLocked?: string | null;
  /** The themes the document's class may use; null for all. */
  lookThemes?: ThemeId[] | null;
}

const titleCase = (id: string) => id.charAt(0).toUpperCase() + id.slice(1);

const formats = [
  { labelKey: "export.pdf", value: "PDF", descKey: "export.pdfDesc", feature: "exportPdf" },
  { labelKey: "export.latex", value: "LATEX", descKey: "export.latexDesc", feature: "exportLatex" },
  { labelKey: "export.markdown", value: "MARKDOWN", descKey: "export.markdownDesc", feature: "exportMarkdown" },
  { labelKey: "export.html", value: "HTML", descKey: "export.htmlDesc", feature: "exportHtml" },
  { labelKey: "export.lilia", value: "LML", descKey: "export.liliaDesc", feature: null },
  { labelKey: "export.word", value: "DOCX", descKey: "export.wordDesc", feature: "exportDocx" },
  // Typst deactivated — Phase 3 (LILIA-33)
  // { labelKey: "export.typst", value: "TYPST", descKey: "export.typstDesc", feature: "exportTypst" },
] as const;

export function ExportDialog({
  open,
  onClose,
  documentId,
  documentTitle,
  look: documentLookRaw = null,
  lookLocked = null,
  lookThemes = null,
}: ExportDialogProps) {
  const { fetchWithAuth } = useApiContext();
  const { hasFeature, requiresUpgrade } = useLicense();
  const { t } = useTranslation('dialogs');
  const [format, setFormat] = useState("PDF");
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // LaTeX options
  const [includeTableOfContents, setIncludeTableOfContents] = useState(true);
  const [latexStructure, setLatexStructure] = useState<"single" | "multi">("single");
  const [latexMultiFileLayout, setLatexMultiFileLayout] = useState<"flat" | "overleaf">("flat");

  // HTML options
  const [standalone, setStandalone] = useState(true);
  const [includeStyles, setIncludeStyles] = useState(true);
  const [includeImages, setIncludeImages] = useState(false);

  // PDF engine choice — auto picks Typst with pdflatex fallback (fastest);
  // typst forces Typst-only; pdflatex skips Typst entirely (faithful to
  // user's hand-edited LaTeX).
  const [pdfEngine, setPdfEngine] = useState<PdfEngine>("auto");

  // The Export PDF row (design-handoffs/2026-10-05-document-themes, 1e):
  // "Look: Index ▾" and Print-safe, for this export only.
  const locked = !!lookLocked;
  const documentLook: ThemeId = effectiveTheme(documentLookRaw, lookLocked, lookThemes);
  const [exportLook, setExportLook] = useState<ThemeId>(documentLook);
  const [printSafe, setPrintSafe] = useState(false);
  // The accessible PDF (1 Nov 2026): tagged for screen readers, always through LaTeX, so the engine choice gives way.
  const [accessible, setAccessible] = useState(false);
  const { themes } = useThemes({ enabled: open && !locked });
  useEffect(() => {
    if (open) {
      setExportLook(documentLook);
      setPrintSafe(false);
    }
  }, [open, documentLook]);
  // Until the list loads (or if it can't), the document's own look is the
  // only option, which exports exactly as today.
  const lookOptions: { id: ThemeId; name: string; reason: string | null }[] = themes.length
    ? themes
        .filter((t) => themeAllowed(t.id, lookThemes))
        .map((t) => ({ id: t.id, name: t.name, reason: unavailableReason(t) }))
    : [{ id: documentLook, name: titleCase(documentLook), reason: null }];

  // Round-trip integrity counters (LaTeX export only). Lazy-loaded on
  // LaTeX selection so the dialog opens instantly for other formats.
  // `passthrough` = code blocks with language="latex"; those round-trip
  // verbatim on export (import created them from \begin{...}\end{...}
  // we didn't have a Lilia block for, and the export puts them back).
  const [roundTripCounts, setRoundTripCounts] = useState<{
    passthrough: number;
    totalBlocks: number;
  } | null>(null);
  const [roundTripLoading, setRoundTripLoading] = useState(false);

  useEffect(() => {
    if (format !== "LATEX" || !open) return;
    if (roundTripCounts !== null) return;
    let active = true;
    (async () => {
      setRoundTripLoading(true);
      try {
        const res = await fetchWithAuth(`${API_BASE}/documents/${documentId}`);
        if (!res.ok) return;
        const data = await res.json();
        const blocks = (data.blocks ?? []) as Array<{ type?: string; content?: Record<string, unknown> }>;
        const passthrough = blocks.filter(
          b => b.type === "code" && (b.content as { language?: string } | undefined)?.language === "latex"
        ).length;
        if (active) setRoundTripCounts({ passthrough, totalBlocks: blocks.length });
      } catch {
        // Silent: confirmation is informational, absence is acceptable.
      } finally {
        if (active) setRoundTripLoading(false);
      }
    })();
    return () => { active = false; };
  }, [format, open, documentId, fetchWithAuth, roundTripCounts]);

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Read a JSON error message out of a failed response, falling back to
  // the status code. Both ExportPdf and ExportLaTeX return { message }
  // on 5xx; other endpoints just return text.
  const errorFromResponse = async (response: Response): Promise<string> => {
    try {
      const ct = response.headers.get("content-type") ?? "";
      if (ct.includes("application/json")) {
        const body = await response.json();
        if (typeof body?.message === "string") return body.message;
        if (typeof body?.error === "string") return body.error;
      } else {
        const text = await response.text();
        if (text) return text.slice(0, 300);
      }
    } catch {
      // Fallthrough to status-only message.
    }
    return `Export failed (${response.status})`;
  };

  const firstExport = useFirstExportToast();
  const handleExport = async () => {
    setIsExporting(true);
    setProgress(10);
    setError(null);

    const safeTitle = documentTitle || "document";

    try {
      // Build the API URL + filename per format. All exporters live on
      // the C# API — the editor SPA just fetches and downloads.
      let endpoint: string;
      let filename: string;

      if (format === "LATEX") {
        // Overleaf layout requires multi-file structure to ship a useful project
        const effectiveStructure = latexMultiFileLayout === "overleaf" ? "multi" : latexStructure;
        const params = new URLSearchParams({
          structure: effectiveStructure,
          multiFileLayout: latexMultiFileLayout,
          includeImages: "true",
        });
        endpoint = `${API_BASE}/documents/${documentId}/export/latex?${params}`;
        filename = `${safeTitle}.zip`;
      } else if (format === "PDF") {
        const params = pdfExportParams({ engine: pdfEngine, look: exportLook, documentLook, printSafe, locked, accessible });
        endpoint = `${API_BASE}/documents/${documentId}/export/pdf?${params}`;
        filename = `${safeTitle}.pdf`;
      } else if (format === "HTML") {
        const params = new URLSearchParams({ bundleImages: includeImages ? "true" : "false" });
        endpoint = `${API_BASE}/documents/${documentId}/export/html?${params}`;
        filename = includeImages ? `${safeTitle}.zip` : `${safeTitle}.html`;
      } else if (format === "MARKDOWN") {
        endpoint = `${API_BASE}/documents/${documentId}/export/markdown`;
        filename = `${safeTitle}.md`;
      } else if (format === "LML") {
        endpoint = `${API_BASE}/documents/${documentId}/export/lml`;
        filename = `${safeTitle}.lml`;
      } else if (format === "DOCX") {
        endpoint = `${API_BASE}/documents/${documentId}/export/docx`;
        filename = `${safeTitle}.docx`;
      } else {
        throw new Error(`Unsupported format: ${format}`);
      }

      setProgress(40);
      const response = await fetchWithAuth(endpoint);
      if (!response.ok) {
        throw new Error(await errorFromResponse(response));
      }
      setProgress(85);
      const blob = await response.blob();
      downloadBlob(blob, filename);
      setProgress(100);
      firstExport(format);

      setTimeout(() => onClose(), 500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export failed");
      setProgress(0);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[500px]" data-testid="export-dialog">
        <DialogHeader>
          <DialogTitle>{t('export.title')}</DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <div className="flex flex-col gap-2 mb-4">
            {formats.map((f) => {
              const locked = f.feature !== null && !hasFeature(f.feature);
              const upgradeTier = f.feature !== null ? requiresUpgrade(f.feature) : null;
              return (
                <div
                  key={f.value}
                  className={cn(
                    "flex items-center gap-3 p-3 border-2 border-[var(--lilia-border)] rounded-lg transition-all duration-150",
                    locked
                      ? "opacity-60 cursor-not-allowed"
                      : "cursor-pointer hover:border-[var(--lilia-primary)] hover:bg-[var(--lilia-surface-hover)]",
                    format === f.value && !locked && "border-[var(--lilia-primary)] bg-[var(--lilia-primary-light)]"
                  )}
                  onClick={() => !locked && setFormat(f.value)}
                >
                  <div>
                    <input
                      type="radio"
                      checked={format === f.value}
                      onChange={() => !locked && setFormat(f.value)}
                      disabled={locked}
                      className="w-[18px] h-[18px] accent-[var(--lilia-primary)]"
                    />
                  </div>
                  <div className="flex-1">
                    <strong className="block text-sm text-[var(--lilia-text)]">{t(f.labelKey)}</strong>
                    <span className="text-xs text-[var(--lilia-text-muted)]">{t(f.descKey)}</span>
                  </div>
                  {locked && upgradeTier && (
                    <a
                      href="/pricing"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 px-2.5 py-1 text-[11px] font-medium rounded-full bg-[var(--lilia-primary-light)] text-[var(--lilia-primary)] hover:underline"
                    >
                      {upgradeTier === "converter" ? t('export.converter') : t('common:pro')}
                    </a>
                  )}
                </div>
              );
            })}
          </div>

          {format === "LATEX" && (
            <div className="p-4 bg-[var(--lilia-bg)] rounded-lg mb-4 space-y-3">
              <h4 className="text-[13px] font-semibold text-[var(--lilia-text)] mb-3">{t('export.latexOptions')}</h4>
              <div className="space-y-2">
                <span className="text-sm font-medium">{t('export.projectStructure')}</span>
                <div className="flex gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={latexStructure === "single"}
                      onChange={() => setLatexStructure("single")}
                      className="w-[16px] h-[16px] accent-[var(--lilia-primary)]"
                    />
                    <span className="text-sm">{t('export.singleFile')}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={latexStructure === "multi"}
                      onChange={() => setLatexStructure("multi")}
                      className="w-[16px] h-[16px] accent-[var(--lilia-primary)]"
                    />
                    <span className="text-sm">{t('export.multiFile')}</span>
                  </label>
                </div>
              </div>
              {latexStructure === "multi" && (
                <div className="space-y-2">
                  <span className="text-sm font-medium">{t('export.layout')}</span>
                  <div className="flex gap-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={latexMultiFileLayout === "flat"}
                        onChange={() => setLatexMultiFileLayout("flat")}
                        className="w-[16px] h-[16px] accent-[var(--lilia-primary)]"
                      />
                      <span className="text-sm">{t('export.flatLayout')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={latexMultiFileLayout === "overleaf"}
                        onChange={() => { setLatexMultiFileLayout("overleaf"); setLatexStructure("multi"); }}
                        className="w-[16px] h-[16px] accent-[var(--lilia-primary)]"
                      />
                      <span className="text-sm">{t('export.overleafStyle')}</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Round-trip integrity confirmation — integration plan §Surface 4.
                  Only rendered when we've managed to fetch the block count;
                  silent on fetch failure (informational, not a gate). */}
              {roundTripLoading && (
                <div className="pt-3 border-t border-[var(--lilia-border)] text-[12px] text-[var(--lilia-text-muted)]">
                  Checking export integrity…
                </div>
              )}
              {!roundTripLoading && roundTripCounts !== null && (
                <div className="pt-3 border-t border-[var(--lilia-border)]">
                  {roundTripCounts.passthrough > 0 ? (
                    <div className="flex items-start gap-2 text-[12px]">
                      <svg viewBox="0 0 24 24" width="14" height="14" className="mt-0.5 shrink-0 text-emerald-600" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                      </svg>
                      <div className="text-[var(--lilia-text)]">
                        <span className="font-semibold">{roundTripCounts.passthrough}</span>{" "}
                        block{roundTripCounts.passthrough === 1 ? "" : "s"} contain passthrough LaTeX — exports verbatim, nothing gets lost.
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 text-[12px] text-[var(--lilia-text-muted)]">
                      <svg viewBox="0 0 24 24" width="14" height="14" className="mt-0.5 shrink-0 text-emerald-600" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                      </svg>
                      <span>All {roundTripCounts.totalBlocks} block{roundTripCounts.totalBlocks === 1 ? "" : "s"} render natively — no passthrough needed.</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {format === "HTML" && (
            <div className="p-4 bg-[var(--lilia-bg)] rounded-lg mb-4 space-y-2">
              <h4 className="text-[13px] font-semibold text-[var(--lilia-text)] mb-3">{t('export.htmlOptions')}</h4>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={standalone}
                  onCheckedChange={(checked) => setStandalone(checked === true)}
                />
                <span className="text-sm">{t('export.standalone')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={includeStyles}
                  onCheckedChange={(checked) => setIncludeStyles(checked === true)}
                />
                <span className="text-sm">{t('export.includeStyles')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={includeImages}
                  onCheckedChange={(checked) => setIncludeImages(checked === true)}
                />
                <span className="text-sm">{t('export.includeImages')}</span>
              </label>
            </div>
          )}

          {format === "PDF" && (
            <div className="p-4 bg-[var(--lilia-bg)] rounded-lg mb-4 space-y-2">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pb-3 mb-1 border-b border-[var(--lilia-border)]">
                <label className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-[var(--lilia-text)]">Look</span>
                  <select
                    data-testid={testIds.look.exportLook}
                    value={exportLook}
                    disabled={locked}
                    title={lookLocked ?? "This export only; the document keeps its theme."}
                    onChange={(e) => setExportLook(e.target.value as ThemeId)}
                    className="h-8 rounded-md border border-[var(--lilia-border-strong)] bg-[var(--lilia-surface)] text-[var(--lilia-text)] text-[13px] px-2 focus:outline-none focus:ring-1 focus:ring-[var(--lilia-primary)] disabled:opacity-60"
                  >
                    {lookOptions.map((t) => (
                      <option key={t.id} value={t.id} disabled={!!t.reason && t.id !== exportLook}>
                        {t.reason ? `${t.name} (${t.reason.toLowerCase()})` : t.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch
                    data-testid={testIds.look.exportPrintSafe}
                    checked={printSafe}
                    onCheckedChange={setPrintSafe}
                    aria-describedby="export-pdf-print-safe-hint"
                  />
                  <span className="font-medium text-[var(--lilia-text)]">Print-safe</span>
                </label>
                <span id="export-pdf-print-safe-hint" className="basis-full text-[12px] text-[var(--lilia-text-muted)]">
                  {locked
                    ? lookLocked
                    : "This export only. Print-safe prints on white paper, and dark grounds take their light pair."}
                </span>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch
                    data-testid={testIds.look.exportAccessible}
                    checked={accessible}
                    onCheckedChange={setAccessible}
                    aria-describedby="export-pdf-accessible-hint"
                  />
                  <span className="font-medium text-[var(--lilia-text)]">{t("export.accessiblePdf")}</span>
                </label>
                <span id="export-pdf-accessible-hint" className="basis-full text-[12px] text-[var(--lilia-text-muted)]">
                  {t("export.accessiblePdfHint")}
                </span>
              </div>
              <fieldset disabled={accessible} className="disabled:opacity-50 space-y-2">
              <h4 className="text-[13px] font-semibold text-[var(--lilia-text)] mb-2">PDF engine</h4>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={pdfEngine === "auto"}
                  onChange={() => setPdfEngine("auto")}
                  className="mt-1 w-[16px] h-[16px] accent-[var(--lilia-primary)]"
                />
                <span className="text-sm">
                  <span className="font-medium">Auto</span>
                  <span className="block text-[12px] text-[var(--lilia-text-muted)]">Typst when possible (fast), pdflatex fallback. Recommended.</span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={pdfEngine === "typst"}
                  onChange={() => setPdfEngine("typst")}
                  className="mt-1 w-[16px] h-[16px] accent-[var(--lilia-primary)]"
                />
                <span className="text-sm">
                  <span className="font-medium">Typst only</span>
                  <span className="block text-[12px] text-[var(--lilia-text-muted)]">Sub-second compile. Fails if the document uses unsupported LaTeX.</span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={pdfEngine === "pdflatex"}
                  onChange={() => setPdfEngine("pdflatex")}
                  className="mt-1 w-[16px] h-[16px] accent-[var(--lilia-primary)]"
                />
                <span className="text-sm">
                  <span className="font-medium">pdflatex</span>
                  <span className="block text-[12px] text-[var(--lilia-text-muted)]">Classic engine — slower but faithful to hand-edited LaTeX.</span>
                </span>
              </label>
              </fieldset>
            </div>
          )}

          {isExporting && (
            <div className="mt-4 text-center">
              <Progress value={progress} />
              <span className="block mt-2 text-[13px] text-[var(--lilia-text-muted)]">{t('export.exporting')}</span>
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-[#fef2f2] border border-[#fecaca] rounded-lg text-[var(--lilia-danger)] text-[13px]">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isExporting}>
            {t('common:cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? t('common:exporting') : t('common:export')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
